import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { AppShell } from "../components/AppShell";
import { Card, ErrorBox, Skeleton } from "../components/ui";
import { useContractor, useContractorMutation, useContractorOverview } from "../lib/api";
import { fmtNumber } from "../lib/format";
import { GATE_PILL, STATUS_PILL } from "./contractors/shared";

const MILESTONE_PILL: Record<string, string> = {
  Pending: "bg-brand-grey-light text-brand-charcoal", Certified: "bg-blue-50 text-brand-primary",
  Invoiced: "bg-green-50 text-status-green", Rejected: "bg-red-50 text-status-red",
};


/** One contractor in full (WP-23.1, OS-0617): the access gate and why, engagements with their cost against
 * budget, papers, the people on site, and — for a firm — the delivery history Procurement and Projects keep.
 *
 * Promoted from a drawer over the board to its own address, so a blocked contractor can be linked to directly:
 * the gate decision is the thing a site manager needs to send somebody, and a drawer cannot be sent. */
export default function ContractorDetail() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const { name = "" } = useParams();
  const ov = useContractorOverview();
  const money = (v: number | null | undefined) => (v == null ? "–" : `${fmtNumber(v / 1e6, lng, 1)} M CDF`);
  return (
    <AppShell subtitle={t("con.title")}>
      <ContractorPanel name={decodeURIComponent(name)} money={money} canManage={ov.data?.permissions.can_manage ?? false} />
    </AppShell>
  );
}

function ContractorPanel({ name, money, canManage }: { name: string; money: (v: number | null | undefined) => string; canManage: boolean }) {
  const { t } = useTranslation();
  const q = useContractor(name);
  const mutation = useContractorMutation();
  const d = q.data;
  const c = (d?.contractor ?? {}) as Record<string, unknown>;

  return (
    <Card className="p-5" data-con-panel>
      <div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-semibold">{String(c.contractor_name ?? name)}</h2>
            <p className="text-xs text-brand-grey">
              {c.contractor_type ? t(`con.type.${String(c.contractor_type)}`) : ""}{c.trade ? ` · ${String(c.trade)}` : ""}
              {c.company ? ` · ${String(c.company)}` : ""}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {canManage && (
              <Link to={`/contractors/${encodeURIComponent(name)}/edit`} data-con-edit
                className="rounded border border-brand-grey-light px-3 py-1 text-xs font-semibold hover:border-brand-primary">
                {t("emp.form.edit")}
              </Link>
            )}
            <Link to="/contractors" className="text-xs font-semibold text-brand-primary hover:underline">← {t("con.backToBoard")}</Link>
          </div>
        </div>

        {q.isLoading && <div className="mt-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>}
        {q.isError && <div className="mt-4"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>}

        {d && (
          <>
            <div className={`mt-4 rounded-lg px-3 py-2 text-sm font-semibold ${GATE_PILL[d.access.gate]}`} data-con-gate>
              {t(`con.gate.${d.access.gate}`)} — {d.access.gate === "Green" ? t("con.access.mayWork") : d.access.blocking.length ? t("con.access.blockedBy", { what: d.access.blocking.join(", ") }) : t("con.access.watch", { what: [...d.access.expiring, ...d.access.expired].join(", ") })}
            </div>

            <h3 className="mt-5 font-heading text-sm font-semibold">{t("con.panel.engagements")}</h3>
            {d.engagements.length === 0 && <p className="mt-1 text-xs text-brand-grey">{t("con.panel.noEngagements")}</p>}
            <ul className="mt-2 space-y-2">
              {d.engagements.map((e) => {
                const cost = d.cost_by_engagement[e.name];
                return (
                  <li key={e.name} className="rounded border border-brand-grey-light p-3 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{e.title}</span>
                      <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${STATUS_PILL[e.status] ?? ""}`}>{t(`con.status.${e.status}`, e.status)}</span>
                    </div>
                    <div className="mt-1 text-brand-grey">
                      {e.start_date} → {e.end_date}
                      {e.billing_type === "Fixed price"
                        ? ` · ${t("con.billing.fixed")}`
                        : e.rate ? ` · ${money(e.rate)} / ${t(`con.basis.${e.rate_basis ?? "Day"}`, e.rate_basis ?? "")}` : ""}
                      {e.plant ? ` · ${e.plant}` : ""}{e.project ? ` · ${e.project}` : ""}
                    </div>
                    {cost?.schedule && (
                      <div className="mt-2 rounded border border-brand-grey-light/70 bg-[#F9FAFB] p-2" data-con-schedule={e.name}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-semibold">{t("con.panel.schedule")}</span>
                          <span className="text-brand-grey">
                            {t("con.panel.certifiedOf", { certified: money(cost.schedule.certified), price: money(cost.schedule.price) })}
                            {cost.schedule.progress_pct != null ? ` · ${cost.schedule.progress_pct} %` : ""}
                          </span>
                        </div>
                        {!cost.schedule.reconciles && (
                          <div className="mt-1 text-[11px] font-semibold text-status-amber">
                            {t("con.panel.scheduleMismatch", { amount: money(Math.abs(cost.schedule.difference)) })}
                          </div>
                        )}
                        <ul className="mt-1">
                          {(e.milestones ?? []).map((m) => (
                            <li key={m.name} className="flex flex-wrap items-center justify-between gap-2 border-t border-brand-grey-light/60 py-1" data-con-milestone={m.name}>
                              <span className="min-w-0 flex-1 truncate">
                                {m.title}
                                <span className="ml-2 text-brand-grey">{m.due_date ?? ""}</span>
                              </span>
                              <span className="tabular-nums">{money(m.amount)}</span>
                              <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${MILESTONE_PILL[m.status] ?? ""}`}>
                                {t(`con.milestone.${m.status}`, m.status)}
                              </span>
                              {canManage && m.status === "Pending" && (
                                <span className="flex gap-1">
                                  <button disabled={mutation.isPending} data-con-certify={m.name}
                                    onClick={() => mutation.mutate({ method: "certify_milestone", params: { engagement: e.name, milestone: m.name, status: "Certified" } })}
                                    className="rounded bg-brand-primary px-2 py-0.5 font-semibold text-white disabled:opacity-50">{t("con.panel.certify")}</button>
                                  <button disabled={mutation.isPending}
                                    onClick={() => mutation.mutate({ method: "certify_milestone", params: { engagement: e.name, milestone: m.name, status: "Rejected" } })}
                                    className="rounded border border-brand-grey-light px-2 py-0.5 hover:border-status-red">{t("con.panel.rejectWork")}</button>
                                </span>
                              )}
                              {canManage && m.status === "Certified" && (
                                <button disabled={mutation.isPending} data-con-invoiced={m.name}
                                  onClick={() => mutation.mutate({ method: "certify_milestone", params: { engagement: e.name, milestone: m.name, status: "Invoiced" } })}
                                  className="rounded border border-brand-grey-light px-2 py-0.5">{t("con.panel.markInvoiced")}</button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {canManage && e.posting && (e.posting.to_timesheet.logs > 0 || e.posting.to_invoice.lines > 0) && (
                      <div className="mt-2 flex flex-wrap items-center gap-3 rounded border border-brand-primary/30 bg-blue-50/40 p-2" data-con-posting={e.name}>
                        {e.posting.to_timesheet.logs > 0 && (
                          <>
                            <span>{t("con.posting.toProject", { days: e.posting.to_timesheet.days, amount: money(e.posting.to_timesheet.amount) })}</span>
                            <button disabled={mutation.isPending || !e.posting.project} data-con-post-timesheet={e.name}
                              title={e.posting.project ? undefined : t("con.posting.noProject")}
                              onClick={() => mutation.mutate({ method: "post_to_timesheet", params: { engagement: e.name } })}
                              className="rounded bg-brand-primary px-2 py-1 font-semibold text-white disabled:opacity-40">{t("con.posting.post")}</button>
                            {!e.posting.project && <span className="text-[11px] text-status-amber">{t("con.posting.noProject")}</span>}
                          </>
                        )}
                        {e.posting.to_invoice.lines > 0 && (
                          <>
                            <span>{t("con.posting.toInvoice", { amount: money(e.posting.to_invoice.amount) })}</span>
                            <button disabled={mutation.isPending || !e.posting.supplier} data-con-draft-invoice={e.name}
                              title={e.posting.supplier ? undefined : t("con.posting.noSupplier")}
                              onClick={() => mutation.mutate({ method: "draft_invoice", params: { engagement: e.name } })}
                              className="rounded border border-brand-primary px-2 py-1 font-semibold text-brand-primary disabled:opacity-40">{t("con.posting.draft")}</button>
                          </>
                        )}
                      </div>
                    )}
                    {cost && (
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <span>{t("con.panel.booked")}: <span className="tabular-nums font-medium">{money(cost.approved_amount)}</span></span>
                        {cost.pending_amount > 0 && <span className="text-status-amber">{t("con.panel.awaiting")}: <span className="tabular-nums">{money(cost.pending_amount)}</span></span>}
                        {cost.budget != null && (
                          <span className={cost.over_budget ? "font-semibold text-status-red" : "text-brand-grey"}>
                            {t("con.panel.ofBudget", { pct: cost.utilisation_pct ?? 0, budget: money(cost.budget) })}
                            {cost.over_budget ? ` — ${t("con.panel.overBudget")}` : ""}
                          </span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <h3 className="mt-5 font-heading text-sm font-semibold">{t("con.panel.documents")}</h3>
            <ul className="mt-2 text-xs">
              {d.documents.map((doc) => (
                <li key={`${doc.source}-${doc.name}`} className="flex items-center justify-between gap-2 border-t border-brand-grey-light/70 py-1">
                  <span>
                    {doc.document_type}
                    {doc.source === "employee" && <span className="ml-2 rounded bg-brand-grey-light px-1.5 py-0.5 text-[10px] text-brand-charcoal">{t("con.panel.fromEmployeeFile")}</span>}
                  </span>
                  <span className="text-brand-grey">{doc.expires_on ? `${t("con.panel.expires")} ${doc.expires_on}` : t("con.panel.noExpiry")} · {doc.status}</span>
                </li>
              ))}
              {d.documents.length === 0 && <li className="py-1 text-brand-grey">{t("con.panel.noDocuments")}</li>}
            </ul>
            <p className="mt-1 text-[11px] text-brand-grey">{t("con.panel.required", { list: d.access.required.join(", ") })}</p>

            {d.workers.length > 0 && (
              <>
                <h3 className="mt-5 font-heading text-sm font-semibold">{t("con.panel.workers", { count: d.workers.length })}</h3>
                <ul className="mt-2 text-xs">
                  {d.workers.map((w) => (
                    <li key={w.name} className="flex justify-between border-t border-brand-grey-light/70 py-1">
                      <span>{w.contractor_name}</span><span className="text-brand-grey">{w.trade || "–"}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {(d.qualification.length > 0 || d.deliverables.length > 0) && (
              <>
                <h3 className="mt-5 font-heading text-sm font-semibold">{t("con.panel.performance")}</h3>
                <p className="text-[11px] text-brand-grey">{t("con.panel.performanceHint")}</p>
                <ul className="mt-2 text-xs">
                  {d.qualification.map((qa) => (
                    <li key={qa.name} className="flex justify-between border-t border-brand-grey-light/70 py-1">
                      <span>{qa.item_group || "–"} · {qa.status}</span>
                      <span className="text-brand-grey tabular-nums">{t("con.panel.onTime")} {qa.on_time_pct ?? "–"}% · {t("con.panel.quality")} {qa.quality_pct ?? "–"}%</span>
                    </li>
                  ))}
                  {d.deliverables.slice(0, 6).map((dv) => (
                    <li key={dv.name} className="flex justify-between border-t border-brand-grey-light/70 py-1">
                      <span className="truncate">{dv.title}</span>
                      <span className="text-brand-grey">{dv.status}{dv.due_date ? ` · ${dv.due_date}` : ""}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {canManage && d.logs.some((l) => l.status === "Submitted") && (
              <>
                <h3 className="mt-5 font-heading text-sm font-semibold">{t("con.panel.awaitingApproval")}</h3>
                <ul className="mt-2 text-xs">
                  {d.logs.filter((l) => l.status === "Submitted").map((l) => (
                    <li key={l.name} className="flex items-center justify-between gap-2 border-t border-brand-grey-light/70 py-1.5">
                      <span>{l.log_date} · {l.quantity} × {money(l.rate)} = <span className="font-medium tabular-nums">{money(l.amount)}</span>
                        {l.contractor && l.contractor !== name && <span className="ml-2 text-brand-grey">{l.contractor}</span>}</span>
                      <span className="flex gap-2">
                        <button disabled={mutation.isPending} data-con-approve
                          onClick={() => mutation.mutate({ method: "decide_time", params: { name: l.name, status: "Approved" } })}
                          className="rounded bg-brand-primary px-2 py-1 font-semibold text-white disabled:opacity-50">{t("con.panel.approve")}</button>
                        <button disabled={mutation.isPending}
                          onClick={() => mutation.mutate({ method: "decide_time", params: { name: l.name, status: "Rejected" } })}
                          className="rounded border border-brand-grey-light px-2 py-1 hover:border-status-red">{t("con.panel.reject")}</button>
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
